import React, { useEffect, useReducer, useState } from 'react';
import { useSelector } from 'react-redux';

import { useSaveSettings } from '../../hooks/useSaveSettings';
import useSearch from '../../hooks/useSearch';
import useFilters from '../../hooks/useFilters';
import { fieldsReducer, initFieldsState, mapToSettings } from './silverfortNodesReducer';
import { useTabs } from '../../hooks/useTabs';

import SilverfortNode from '../../components/SilverfortNode/SilverfortNode';
import { CATEGORIES } from '../../common/categories';
import Category from '../../components/Category/Category';
import { NODE_FIELDS, PROTECTED_DOMAIN_FIELDS, COMMON_FIELDS } from '../../common/fields';
import { FILER_TYPES, UNASSIGNED_VALUE } from '../../common/filterTypes';
import { ROLES, USER_ENGAGEMENT_PAGE_KEYS } from '@silverfort-nodelibs/common/utils/consts';

import FiltersModal from '../FiltersModal/FiltersModal';

import styles from './SilverfortNodes.module.css';
import { httpPost, isUserPermitted } from '../../../utils';
import { isNodeHasProperAz } from '../../common/utils';

const { LDAP_IP_OPTIONS } = COMMON_FIELDS;
const { SILVERFORT_NODES } = CATEGORIES;

const SilverfortNodes = ({ saveFunc, disableSave }) => {
    const settings = useSelector((state) => state.settings.settings);
    const user = useSelector((state) => state.userData.user);
    const isEditor = isUserPermitted(user, 'settings', ROLES.EDITOR.value);

    const domainsOptions = {};
    Object.values(settings.DomainsConfigurations || {}).forEach((domain) => {
        domainsOptions[domain.id] = {
            value: domain.id,
            label: domain.DomainName,
            serviceAccountUser: domain.ServiceAccountUsername,
            isCert: !!domain.rootCACertificateData.data
        };
    });
    const baseFiltersData = {
        [NODE_FIELDS.NODE_NAME.ID]: {
            value: '',
            type: FILER_TYPES.INPUT,
            name: NODE_FIELDS.NODE_NAME.NAME
        },
        [NODE_FIELDS.DOMAINS.ID]: {
            value: null,
            type: FILER_TYPES.SELECT,
            options: [{ label: 'Unassigned', value: UNASSIGNED_VALUE }, ...Object.values(domainsOptions)],
            name: NODE_FIELDS.DOMAINS.NAME,
            arrayId: NODE_FIELDS.DOMAIN.ID,
            clearable: true
        }
    };

    const [showFilterModal, setShowFilterModal] = useState(false);
    const [filtersData, setFiltersData] = useState(baseFiltersData);

    const [fields, dispatch] = useReducer(fieldsReducer, { settings }, initFieldsState);
    const { ldapIpOptions, ...nodeFields } = fields;

    const visibleFields = useSearch(nodeFields);
    const [isFiltered, filteredFields] = useFilters(visibleFields, filtersData);

    const [selectedTab, setSelectedTab] = useTabs(SILVERFORT_NODES, filteredFields);
    const [save, discard] = useSaveSettings(saveFunc, settings, mapToSettings, fields, selectedTab, dispatch);

    for (const nodeId in nodeFields) {
        const currentNodeFields = nodeFields[nodeId].fields;
        const nodeDomainsFields = currentNodeFields[NODE_FIELDS.DOMAINS.ID].value;
        for (const domainFields of nodeDomainsFields) {
            const domainId = domainFields[NODE_FIELDS.DOMAIN.ID].value;
            if (domainId && domainsOptions[domainId] && !domainsOptions[domainId].serviceAccountUser) {
                const nodeDomainServiceAccount = domainFields[PROTECTED_DOMAIN_FIELDS.DOMAIN_SERVICE_ACCOUNT.ID].value;
                if (nodeDomainServiceAccount && nodeDomainServiceAccount.user) {
                    const newDomainOption = { ...domainsOptions[domainId] };
                    newDomainOption.serviceAccountUser = nodeDomainServiceAccount.user;
                    domainsOptions[domainId] = newDomainOption;
                }
            }
        }
    }
    let content = null;
    if (filteredFields[selectedTab]) {
        const isProperAz = isNodeHasProperAz(settings.NodesConfigurations[selectedTab], settings.azConfig);
        content = (
            <SilverfortNode
                nodeId={selectedTab}
                nodeIp={settings.NodesConfigurations[selectedTab].ipAddress}
                isProperAz={isProperAz}
                fields={filteredFields[selectedTab].fields}
                dispatch={dispatch}
                domainsOptions={domainsOptions}
                ldapServerOptions={fields[LDAP_IP_OPTIONS.ID].value}
                allowDelete={isEditor}
            />
        );
    } else {
        content = <div className={styles.noNodes}>No nodes to configure</div>;
    }

    useEffect(() => {
        httpPost('/data/userEngagement', { pageId: USER_ENGAGEMENT_PAGE_KEYS.SETTINGS_NODES });
    }, []);

    return (
        <>
            <Category
                category={SILVERFORT_NODES}
                fields={nodeFields}
                visibleFields={filteredFields}
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
                saveSettings={save}
                discard={() => discard(false)}
                discardAll={() => discard(true)}
                isFiltered={isFiltered}
                setShowFilterModal={() => setShowFilterModal(!showFilterModal)}
                disableSave={disableSave}>
                {content}
            </Category>
            {showFilterModal && (
                <FiltersModal
                    currentFilters={filtersData}
                    onSaveClicked={(newFiltersData) => {
                        setFiltersData(newFiltersData);
                        setShowFilterModal(false);
                    }}
                    onDiscardClicked={() => setShowFilterModal(false)}
                    baseFiltersData={baseFiltersData}
                />
            )}
        </>
    );
};

export default SilverfortNodes;
